import React from 'react'
import { Link } from 'react-router-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'

import { JOB_STATUS_LABELS, JOB_STATUS_TONES, isTerminalStatus, jobDisplayName, type PackagingJob } from '../types/job'
import { Badge } from './ui'
import { dateTime } from '../utils/date'
import { plainJobError } from '../utils/jobError'

// The backend sends naive UTC timestamps; parsed as they are they would read as local time.
const when = (iso?: string | null) => (iso ? dateTime(iso) : null)

/**
 * One packaging job as a task-list row: what is being packaged, how far it has got, and where its
 * logs and artifacts are. Named by `jobDisplayName` so it is called the same thing here as on the
 * background-jobs page.
 */
const PackagingJobRow: React.FC<{
  job: PackagingJob
  /** Offers a dismiss control. Absent means the row cannot be dismissed. */
  onDismiss?: (job: PackagingJob) => void
  /** Closes the list before the link navigates. */
  onNavigate?: () => void
}> = ({ job, onDismiss, onNavigate }) => {
  const running = !isTerminalStatus(job.status)
  return (
    <div className="rounded-lg border border-theme-border-primary bg-theme-primary px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-theme-text-primary">{jobDisplayName(job)}</span>
            <Badge tone="slate">Packaging</Badge>
            <Badge tone={JOB_STATUS_TONES[job.status]}>{JOB_STATUS_LABELS[job.status]}</Badge>
          </div>
          {running && (
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={job.progress_pct}
              className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-theme-tertiary"
            >
              <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${job.progress_pct}%` }} />
            </div>
          )}
          {job.error_message && (
            <p className={`mt-1 text-xs ${job.status === 'failed' ? 'text-red-400' : 'text-theme-text-tertiary'}`}>
              {plainJobError(job.error_message)}
            </p>
          )}
          {when(job.completed_at) && (
            <p className="mt-1 text-[11px] text-theme-text-tertiary">Finished {when(job.completed_at)}</p>
          )}
          <Link
            to="/jobs"
            onClick={onNavigate}
            className="mt-1 inline-block text-xs text-theme-accent-blue hover:underline"
          >
            Background jobs
          </Link>
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={() => onDismiss(job)}
            aria-label="Dismiss this packaging job"
            className="shrink-0 rounded-lg p-1 text-theme-text-tertiary hover:bg-theme-tertiary hover:text-theme-text-primary"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

export default PackagingJobRow
